import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import RULDetailsChart from "./RULDetailsChart";
import RULDetailsTable from "./RULDetailsTable";
import RULDetailsSettingsSidebar from "./RULDetailsSettingsSidebar";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import { getRULScatterData } from "../../../../Api/BatteryHealthApi";
import { encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import { dateToDecimalYear } from "../utils/rulXAxis";
import { useRULBuckets, useRULServerBucketUnits, resolveRULCategory } from "../utils/rulBuckets";
import CODE from "../../../../Static/Constants/StatusCodes";
import { ALL_Y_FILTERS, Y_KEY, RUL_BUCKET_KEYS } from "../utils/rulConstants";

const RULDetailsView = ({
  globalFilters = {},
  chartConfig = {},
  onBack,
  allOnboardingLocationsHierarchy = [],
  allFleets = [],
}) => {
  const pagesContent = usePagesContent();
  const rulDetailsConfig = pagesContent?.metaInfo?.BatteryIntelligence?.RULDetails || {};
  const rulBuckets = useRULBuckets();
  const rulServerBucketUnits = useRULServerBucketUnits();
  const filtersConfig    = rulDetailsConfig.globalFilters || {};

  const onDotClick = useCallback((battery) => {
    if (!battery?.batteryID) return;
    const hash = encryptTheParams({ batteryID: battery.batteryID, tabValue: "health" });
    window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
  }, []);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  const yFilters = useMemo(() => {
    const supported = chartConfig?.rulScatterChart?.supportedUnits;
    if (!supported || supported.length === 0) return ALL_Y_FILTERS;
    return ALL_Y_FILTERS.filter((f) => supported.includes(f.key));
  }, [chartConfig]);

  // ─── Chart legend + y-axis state ────────────────────────────────────────────
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [activeYFilter, setActiveYFilter] = useState(() => {
    const supported = chartConfig?.rulScatterChart?.supportedUnits;
    if (supported && supported.length > 0) return supported[0];
    return "months";
  });

  // ─── Sidebar filter state ────────────────────────────────────────────────────
  const [sidebarFilters, setSidebarFilters] = useState({
    operationalStatus: [],
    rulCategories: [],
    rulCycles: [],
    batteryOEM: [],
    vehicleOEM: [],
    configuration: [],
    protocol: [],
    cellChemistry: [],
    location: globalFilters.hierarchicalLocation || [],
    fleets: [],
    manufacturingYear: [],
  });

  // ─── Effective global filters — merge sidebar configuration/location/customer/status ─
  const effectiveGlobalFilters = useMemo(() => {
    const merged = { ...globalFilters };
    if (sidebarFilters.configuration?.length > 0) {
      merged.configuration = sidebarFilters.configuration;
    }
    if (sidebarFilters.location?.length > 0) {
      merged.hierarchicalLocation = sidebarFilters.location;
    }
    // Business segment and customer account both arrive as fleet ids, which is what
    // becomes the fleetIDs request header.
    if (sidebarFilters.fleets?.length > 0) {
      merged.fleets = sidebarFilters.fleets;
    }
    if (sidebarFilters.operationalStatus?.length > 0) {
      merged.operationalStatus = sidebarFilters.operationalStatus;
    }
    return merged;
  }, [globalFilters, sidebarFilters.configuration, sidebarFilters.location, sidebarFilters.fleets, sidebarFilters.operationalStatus]);

  // ─── Scatter chart additional filters ───────────────────────────────────────
  const scatterAdditionalFilters = useMemo(() => {
    const filters = [];
    if (sidebarFilters.manufacturingYear?.length > 0) {
      filters.push({ name: "mfgYear", value: sidebarFilters.manufacturingYear });
    }
    if (sidebarFilters.rulCategories?.length > 0) {
      filters.push({ name: RUL_BUCKET_KEYS.months, value: sidebarFilters.rulCategories });
    }
    if (sidebarFilters.rulCycles?.length > 0) {
      filters.push({ name: RUL_BUCKET_KEYS.cycles, value: sidebarFilters.rulCycles });
    }
    if (sidebarFilters.protocol?.length > 0) {
      filters.push({ name: "protocolName", value: sidebarFilters.protocol });
    }
    if (sidebarFilters.cellChemistry?.length > 0) {
      filters.push({ name: "cellChemistry", value: sidebarFilters.cellChemistry });
    }
    return filters;
  }, [sidebarFilters.manufacturingYear, sidebarFilters.rulCategories, sidebarFilters.rulCycles, sidebarFilters.protocol, sidebarFilters.cellChemistry]);

  // ─── Table additional filters ────────────────────────────────────────────────
  const tableAdditionalFilters = useMemo(() => {
    const filters = [];
    if (sidebarFilters.rulCategories?.length > 0) {
      filters.push({ name: RUL_BUCKET_KEYS.months, value: sidebarFilters.rulCategories });
    }
    if (sidebarFilters.rulCycles?.length > 0) {
      filters.push({ name: RUL_BUCKET_KEYS.cycles, value: sidebarFilters.rulCycles });
    }
    if (sidebarFilters.manufacturingYear?.length > 0) {
      filters.push({ name: "mfgYear", value: sidebarFilters.manufacturingYear });
    }
    // UI key is "protocol" (the label); the wire name is "protocolName", the column
    // getConfigBaseQuery's protocolMeta join exposes.
    if (sidebarFilters.protocol?.length > 0) {
      filters.push({ name: "protocolName", value: sidebarFilters.protocol });
    }
    if (sidebarFilters.cellChemistry?.length > 0) {
      filters.push({ name: "cellChemistry", value: sidebarFilters.cellChemistry });
    }
    return filters;
  }, [sidebarFilters.rulCategories, sidebarFilters.rulCycles, sidebarFilters.manufacturingYear, sidebarFilters.protocol, sidebarFilters.cellChemistry]);

  const handleCategorySelect = useCallback((name) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const handleResetCategories = useCallback(() => {
    setSelectedCategories(new Set());
  }, []);

  // ─── Scatter data fetch ──────────────────────────────────────────────────────
  const [rawBatteries,   setRawBatteries]   = useState([]);
  const [scatterLoading, setScatterLoading] = useState(true);

  // Reset legend selection when unit tab changes (buckets differ per unit)
  useEffect(() => {
    setSelectedCategories(new Set());
  }, [activeYFilter]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setScatterLoading(true);
      try {
        const res = await getRULScatterData(effectiveGlobalFilters, activeYFilter, scatterAdditionalFilters, chartConfig?.rulScatterChart?.supportedUnits ?? []);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items      = res?.response?.data ?? [];
          const activeYKey = Y_KEY[activeYFilter];
          const batteries  = items.map(({ xLabel, metricsArray, metaInfo }) => {
            const getVal    = (type) => metricsArray?.find((m) => m.type === type)?.value ?? null;
            const rawDays   = getVal("RULCalendarAgeDaysPredicted");
            const rulMonths = rawDays != null ? parseFloat((rawDays / 30.44).toFixed(1)) : null;
            const rulCycles = getVal("RULCyclesPredicted");
            const rulKm     = getVal("RULKmPredicted");
            const mfgDate        = metaInfo?.manufacturingDate;
            const mfgYear        = mfgDate ? dateToDecimalYear(new Date(mfgDate)) : null;
            // Missing / invalid / pre-2000 dates have no x: range-bucket batteries without one
            // are not plotted; "RUL Unavailable" ones are pinned to the x-axis minimum.
            const mfgDecimalYear = mfgYear != null && isFinite(mfgYear) && mfgYear >= 2000 ? mfgYear : null;
            return {
              batteryID: xLabel, deviceID: metaInfo?.deviceID, rulMonths, rulCycles, rulKm,
              [activeYKey]: activeYFilter === "months" ? rulMonths : activeYFilter === "cycles" ? rulCycles : rulKm,
              mfgDecimalYear,
              // Raw metaInfo: the active unit's server bucket label is read from it at
              // classification time (resolveRULCategory), only for units the server labels.
              rulMetaInfo:   metaInfo ?? null,
              rulBucketUnit: activeYFilter,
            };
          });
          setRawBatteries(batteries);
        } else {
          if (!cancelled) setRawBatteries([]);
        }
      } catch (_) {
        if (!cancelled) setRawBatteries([]);
      } finally {
        if (!cancelled) setScatterLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [effectiveGlobalFilters, activeYFilter, scatterAdditionalFilters]);

  const classifiedBatteries = useMemo(() => {
    const activeBuckets  = rulBuckets[activeYFilter] ?? [];
    const useServerLabel = rulServerBucketUnits[activeYFilter] === true;
    return rawBatteries.map((b) => ({
      ...b,
      // Server label only for units the server labels (not km) and only for rows fetched
      // for the active unit; otherwise client classification.
      rulCategory: resolveRULCategory(
        b.rulMetaInfo, activeYFilter, b[Y_KEY[activeYFilter]], activeBuckets,
        useServerLabel && b.rulBucketUnit === activeYFilter
      ),
    }));
  }, [rawBatteries, activeYFilter, rulBuckets, rulServerBucketUnits]);

  return (
    <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative", width: "100%" }}>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: isSidebarExpanded ? "calc(100% - 320px)" : "100%",
          minWidth: 0,
          transition: "width 0.3s ease-in-out",
        }}
      >
        {/* ── Back button ───────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            onClick={onBack}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              "&:hover": {
                "& .MuiTypography-root": { color: "#6D72F6 !important" },
                "& .MuiSvgIcon-root": { color: "#6D72F6" },
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
            <Typography variant="fw500s14lh24c2E2E2E">Back</Typography>
          </Box>
        </Box>

        {/* ── Chart card ────────────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "24px 22px",
            border: "1px solid #E6E6E6",
          }}
        >
          {/* Chart header: title + Y-filter + Reset button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#2E2E2E",
                lineHeight: "24px",
                flexShrink: 0,
              }}
            >
              Remaining Useful Life (RUL)
            </Typography>

            {/* Y-axis segmented control */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#f1f1f1fa",
                borderRadius: "8px",
                p: "3px",
                gap: "7px",
                flexShrink: 0,
              }}
            >
              {yFilters.map((filter) => {
                const isActive = activeYFilter === filter.key;
                return (
                  <Box
                    key={filter.key}
                    onClick={() => setActiveYFilter(filter.key)}
                    sx={{
                      px: "12px",
                      py: "4px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      border: isActive ? "1px solid #6D72F6" : "none",
                      bgcolor: isActive ? "#EEF0FF" : "transparent",
                      boxShadow: isActive
                        ? "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)"
                        : "none",
                      transition: "all 0.15s",
                      userSelect: "none",
                      "&:hover": { bgcolor: isActive ? "#EEF0FF" : "rgba(0,0,0,0.04)" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? "#6D72F6" : "#6B7280",
                        lineHeight: "20px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {filter.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              size="small"
              onClick={handleResetCategories}
              disabled={selectedCategories.size === 0}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                minWidth: "auto",
                px: 1.5,
                py: "3px",
                borderRadius: "6px",
                color: selectedCategories.size > 0 ? "#6D72F6" : "#9E9E9E",
                borderColor: selectedCategories.size > 0 ? "#6D72F6" : "#D1D5DB",
                "&:hover": { borderColor: "#6D72F6", bgcolor: "#F5F5FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#9E9E9E" },
              }}
            >
              Reset
            </Button>
          </Box>

          {scatterLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "360px" }}>
              <CartLoader />
            </Box>
          ) : (
            <RULDetailsChart
              batteries={classifiedBatteries}
              selectedCategories={selectedCategories}
              onCategorySelect={handleCategorySelect}
              activeYFilter={activeYFilter}
              supportedUnits={chartConfig?.rulScatterChart?.supportedUnits}
              onDotClick={onDotClick}
            />
          )}
        </Box>

        {/* ── Battery list table ───────────────────────────────────────────── */}
        <RULDetailsTable globalFilters={effectiveGlobalFilters} additionalFilters={tableAdditionalFilters} />
      </Box>

      {/* ── Settings sidebar ─────────────────────────────────────────────────── */}
      <RULDetailsSettingsSidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded((v) => !v)}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        allFleets={allFleets}
        selectedFleetIds={effectiveGlobalFilters.fleets || []}
        filtersConfig={filtersConfig}
        filters={sidebarFilters}
        onFiltersChange={(patch) => setSidebarFilters((prev) => ({ ...prev, ...patch }))}
      />
    </Box>
  );
};

export default RULDetailsView;
